import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { orderBy } from 'lodash';
import ReactTooltip from 'react-tooltip';
import moment from 'moment-timezone';

import ColumnHeader from '../../../../../components/ColumnHeader';
import Img from '../../../../../components/Img';
import {
    acceptanceBucketTypeSortKey,
    acceptanceBucketTypes,
    sportTypeIds,
    utrTypeIds,
} from '../../../../../utils/eventConstants';
import { ratingTypes } from '../../../../../utils/constants';

import { getAcceptanceGridLayout, isPickleBallSportTypeEvent, isTennisSportTypeEvent } from '../../../../../utils/events';

import styles from '../../../event.scss';
import AcceptancePlayerBucket from './AcceptancePlayerBucket';
import Icon from '../../../../../components/Img/Icon';
import { divisionShape } from '../../../../../propTypes';

const AcceptancePlayersList = (props) => {
    const {
        acceptanceData,
        showPlayers,
        isPttEvent,
        isCurrentMemberAdmin,
        showRatingNotLoggedIn,
        utrTypeId,
        isOfficialHSEvent,
        auth,
        sendMessage,
        hideUtrLabel,
        sportTypeId,
        isStripe,
        allowOffPlatformPayments,
        toggleMessagePopUp,
        currentDivision,
        playerMenuOnSelected,
        isRegistrationClosed,
    } = props;

    const { event, playersTabState } = useSelector(({ eventProfile }) => {
        const { event, playersTabState } = eventProfile;
        return { event, playersTabState };
    });
    const [selectedPlayers, setSelectedPlayers] = useState([]);

    const {
        filterQuery = '',
    } = playersTabState || {};

    const eventSchedule = event.data?.eventSchedule || null;
    const eventEndDate = moment.utc(eventSchedule.eventEndUtc).tz(eventSchedule.timeZone?.value).startOf('day');
    const currentDate = moment();
    const isEventFinished = currentDate.isAfter(eventEndDate);

    const shouldShowSwitchDivision = !isEventFinished;

    const isPickleBallSportEvent = isPickleBallSportTypeEvent(sportTypeId);
    const isTennisSportEvent = isTennisSportTypeEvent(sportTypeId);

    const showAgeColumn = isCurrentMemberAdmin && isPttEvent;

    const gridLayout = getAcceptanceGridLayout(isCurrentMemberAdmin, showAgeColumn, isPttEvent);

    const hasQualifyingBucket = acceptanceData?.buckets?.some(bucket => bucket.type === acceptanceBucketTypeSortKey.QUALIFYING);
    const hasAlternateBucket = acceptanceData?.buckets?.some(bucket => bucket.type === acceptanceBucketTypeSortKey.ALTERNATE);

    const buckets = useMemo(() => {
        let allBuckets = [];
        if (playersTabState.selectedStatus?.value === acceptanceBucketTypes.WITHDRAWN) {
            allBuckets = [{
                id: 'withdrawn',
                type: acceptanceBucketTypeSortKey.WITHDRAWN,
                order: acceptanceBucketTypeSortKey.WITHDRAWN,
                players: acceptanceData.withdrawnPlayers || [],
            }];
        } else {
            allBuckets = [
                ...(acceptanceData?.buckets || []),
                {
                    id: 'withdrawn',
                    type: acceptanceBucketTypeSortKey.WITHDRAWN,
                    order: acceptanceBucketTypeSortKey.WITHDRAWN,
                    players: acceptanceData.withdrawnPlayersFromBuckets || [],
                },
            ];
        }

        const updatedBuckets = allBuckets.map(bucket => {
            // filter players in all buckets based on filter query
            const filteredPLayers = bucket.players
                .filter(player => {
                    if (filterQuery) {
                        const playerListFilter = filterQuery.replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&');
                        const regex = new RegExp(playerListFilter, 'i');
                        return Boolean(regex.exec(`${player.firstName} ${player.lastName}`));
                    }
                    return true;
                });

            return {
                ...bucket,
                players: filteredPLayers,
                totalPlayersCount: bucket?.players?.length,
                visiblePlayersCount: filteredPLayers.length,
            };
        })
        .filter(bucket => {
            if (playersTabState.selectedPosition?.value) {
                return acceptanceBucketTypeSortKey[playersTabState.selectedPosition?.value] === bucket.type;
            }

            return true;
        })
        .filter(bucket => bucket.visiblePlayersCount > 0);

        return orderBy(updatedBuckets, ['order'], ['asc']);
    }, [acceptanceData, filterQuery, playersTabState]);

    const visiblePlayers = useMemo(() => {
        return buckets.reduce((players, bucket) => {
            return [...players, ...bucket.players];
        }, []);
    }, [buckets]);

    const totalPlayersVisible = visiblePlayers?.length;

    const areAllVisiblePlayersSelected = useMemo(() => {
        const allVisiblePlayerIds = visiblePlayers.map(p => p.memberId);
        return allVisiblePlayerIds.every(id => selectedPlayers.includes(id));
    }, [selectedPlayers, visiblePlayers]);

    const onSelectEveryPlayerClick = useCallback(() => {
        setSelectedPlayers(visiblePlayers.map(p => p.memberId));
    }, [visiblePlayers]);

    const handleToggleAllClick = useCallback(() => {
        if (areAllVisiblePlayersSelected) {
            setSelectedPlayers([]);
        } else {
            onSelectEveryPlayerClick();
        }
    }, [areAllVisiblePlayersSelected, onSelectEveryPlayerClick]);

    const onPlayerSelectClick = (memberId) => {
        let updatedSelection = [...selectedPlayers];
        if (!selectedPlayers.includes(memberId)) {
            updatedSelection.push(memberId);
        } else {
            updatedSelection = updatedSelection.filter(mid => mid !== memberId);
        }
        setSelectedPlayers(updatedSelection);
    };

    const onClearSelectionClick = () => {
        setSelectedPlayers([]);
    };

    const onMessageClick = () => {
        toggleMessagePopUp(selectedPlayers);
    };

    const areWithDrawnPlayersSelected = useMemo(() => {
        const withdrawnPlayers = acceptanceData?.withdrawnPlayers || [];
        return withdrawnPlayers.filter(player => selectedPlayers.includes(player.memberId)).length > 0;
    }, [selectedPlayers, acceptanceData]);

    const onSwitchDivisionClick = () => {
        if (areWithDrawnPlayersSelected) {
            return;
        }

        props.onSwitchDivisionClick({ memberIds: selectedPlayers, divisionId: playersTabState.divisionId });
    };

    if (!showPlayers && acceptanceData?.loaded && !totalPlayersVisible) {
        return <p data-testid="event-players.acceptance.hidden-msg">Registered players are hidden.</p>;
    }
    if (!acceptanceData?.loaded) {
        return <p data-testid="event-players.acceptance.loading-msg">Players are loading...</p>;
    }
    if (!totalPlayersVisible) {
        return (
            <p data-testid="event-players.waiting-for-players">
                Waiting for players<br />
                Check back to view players registered for this event.
            </p>
        );
    }
    return (
        <>
            <div className="fw300 fs14" data-testid="event-players.acceptance.player-count">
                Showing {totalPlayersVisible} players
            </div>
            <div className={styles.playerListWrapper}>
                <div className="d-flex text-small fw600 player-list-header player-list-header-v3">
                    <div className={`d-flex align-items-center`}>
                        {isCurrentMemberAdmin && (
                            <div className="checkbox-wrapper">
                                <div
                                    className={`checkbox-check clickable ${areAllVisiblePlayersSelected ? 'checked' : ''}`}
                                    onClick={handleToggleAllClick}
                                    data-testid="event-players.acceptance.select-all-checkbox"
                                >
                                    <Img src="/img/icons/checkmark-icon.svg" />
                                </div>
                            </div>
                        )}
                        <div className="w32 mr8">
                            {/* empty div to align header */}
                        </div>
                    </div>
                    {selectedPlayers.length ? (
                        <div className="flex-grow">
                            <div className="row">
                                <span className="text-dark mr16 ml8" data-testid="event-players.acceptance.selected-count">
                                    {selectedPlayers.length} Selected
                                </span> {areAllVisiblePlayersSelected ? <span className="link clickable" onClick={onClearSelectionClick} data-testid="event-players.acceptance.clear-selection-btn">Clear Selection</span> : <span className="link clickable" onClick={onSelectEveryPlayerClick} data-testid="event-players.acceptance.select-all-btn">Select All Players</span>}
                                <span className="ml16 mr16" data-testid="event-players.acceptance.separator">|</span>
                                <span className="link clickable" onClick={onMessageClick} data-testid="event-players.acceptance.message-btn">
                                    <Img alt="message icon mr8" src={'/img/v4/message.svg'} /> Message Selected Players
                                </span>
                                {shouldShowSwitchDivision && (
                                    <span
                                        className={`${!areWithDrawnPlayersSelected ? 'link' : ''} clickable ml16`}
                                        onClick={onSwitchDivisionClick}
                                        data-testid="event-players.switch-division-btn"
                                        data-tip={(() => {
                                            const messages = [];
                                            if (areWithDrawnPlayersSelected) {
                                                messages.push('Withdrawn player(s) cannot be selected to switch divisions.');
                                            }
                                            return messages.join(' ');
                                        })()}
                                    >
                                        <Icon src={'/img/v4/switch.svg'} /> Switch Division
                                    </span>
                                )}
                                <ReactTooltip place="top" type="dark" effect="solid" />
                            </div>
                        </div>
                    ) : (
                        <div className="flex-grow">
                            <div className="row">
                                <div className={`col-12`}>
                                    <div className="d-grid w-100" style={{ gridTemplateColumns: gridLayout.join(' ') }}>
                                        <ColumnHeader
                                            columnName={`Name`}
                                        />
                                        {showAgeColumn && (
                                            <ColumnHeader
                                                columnName="Age"
                                            />
                                        )}
                                        {isTennisSportEvent && (
                                            <>
                                                <ColumnHeader
                                                    columnName={ratingTypes.tennis}
                                                    imgUrl="/img/v3/icons/icon-singles-black.svg"
                                                />
                                                <ColumnHeader
                                                    columnName={ratingTypes.tennis}
                                                    imgUrl="/img/v3/icons/icon-doubles-black.svg"
                                                />
                                            </>
                                        )}
                                        {isPickleBallSportEvent && (
                                            <>
                                                <ColumnHeader
                                                    columnName={ratingTypes.pickleball}
                                                    imgUrl="/img/v3/icons/icon-singles-black.svg"
                                                />
                                                <ColumnHeader
                                                    columnName={ratingTypes.pickleball}
                                                    imgUrl="/img/v3/icons/icon-doubles-black.svg"
                                                />
                                            </>
                                        )}
                                        {isPttEvent && (
                                            <ColumnHeader
                                                columnName="PTT Rank"
                                                data-testid="event-players.acceptance-list.column-header.ptt-rank"
                                            />
                                        )}
                                        {isCurrentMemberAdmin && (
                                            <ColumnHeader
                                                columnName="Reg Date"
                                            />
                                        )}
                                        {isCurrentMemberAdmin && (
                                            <ColumnHeader
                                                columnName="Status"
                                            />
                                        )}
                                        {isCurrentMemberAdmin && (
                                            <ColumnHeader
                                                columnName="Payment"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="bookmark pr16">
                        {/* empty div to align columns */}
                    </div>
                </div>
                {buckets.length > 0 && buckets.map((bucket) => {
                    return (
                        <AcceptancePlayerBucket
                            key={bucket.type}
                            bucket={bucket}
                            addOptionsSpace={true}
                            auth={auth}
                            currentDivisionId={playersTabState.divisionId}
                            currentDivision={currentDivision}
                            gridLayout={gridLayout}
                            hideUtrLabel={hideUtrLabel}
                            isCurrentMemberAdmin={isCurrentMemberAdmin}
                            selectedPlayers={selectedPlayers}
                            onPlayerSelectClick={onPlayerSelectClick}
                            timeZone={eventSchedule.timeZone}
                            isPttEvent={isPttEvent}
                            utrTypeId={utrTypeId}
                            sportTypeId={sportTypeId}
                            isOfficialHSEvent={isOfficialHSEvent}
                            showAgeColumn={showAgeColumn}
                            showRatingNotLoggedIn={showRatingNotLoggedIn}
                            showRegDate={isCurrentMemberAdmin}
                            showRegStatus={isCurrentMemberAdmin}
                            sendMessage={sendMessage}
                            hasQualifyingBucket={hasQualifyingBucket}
                            hasAlternateBucket={hasAlternateBucket}
                            isStripe={isStripe}
                            allowOffPlatformPayments={allowOffPlatformPayments}
                            playerMenuOnSelected={playerMenuOnSelected}
                            isRegistrationClosed={isRegistrationClosed}
                            isAcceptanceListLocked={acceptanceData.isLocked}
                        />
                    );
                })}
            </div>
        </>
    );
};

AcceptancePlayersList.propTypes = {
    acceptanceData: PropTypes.shape({
        loaded: PropTypes.bool,
        buckets: PropTypes.arrayOf(PropTypes.shape({
            order: PropTypes.number,
            type: PropTypes.string,
            players: PropTypes.arrayOf(PropTypes.shape({
                memberId: PropTypes.number.isRequired,
                rank: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                secondaryPosition: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                acceptanceType: PropTypes.string,
                acceptanceListWildcardStateId: PropTypes.number,
            })),
        })),
    }).isRequired,
    allowOffPlatformPayments: PropTypes.bool,
    auth: PropTypes.shape({
        user: PropTypes.shape({
            id: PropTypes.number.isRequired,
        }).isRequired,
    }).isRequired,
    hideUtrLabel: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    currentDivision: divisionShape.isRequired,
    isOfficialHSEvent: PropTypes.bool,
    isPttEvent: PropTypes.bool,
    isRegistrationClosed: PropTypes.bool,
    isStripe: PropTypes.bool,
    selectedDivision: PropTypes.shape({
        id: PropTypes.number.isRequired,
        divisionFormatTypeId: PropTypes.number.isRequired,
    }).isRequired,
    sendMessage: PropTypes.func,
    showPlayers: PropTypes.bool,
    showRatingNotLoggedIn: PropTypes.bool,
    sportTypeId: PropTypes.number,
    toggleMessagePopUp: PropTypes.func,
    playerMenuOnSelected: PropTypes.func.isRequired,
    utrTypeId: PropTypes.number,
    onSwitchDivisionClick: PropTypes.func.isRequired,
};

AcceptancePlayersList.defaultProps = {
    allowOffPlatformPayments: false,
    hideUtrLabel: false,
    isCurrentMemberAdmin: false,
    isOfficialHSEvent: false,
    isPttEvent: false,
    isRegistrationClosed: false,
    isStripe: false,
    sendMessage: () => { },
    showPlayers: false,
    showRatingNotLoggedIn: false,
    sportTypeId: sportTypeIds.TENNIS,
    toggleMessagePopUp: () => { },
    utrTypeId: utrTypeIds.MYUTR,
};

export default AcceptancePlayersList;
